// src/App.jsx
import React, { useEffect } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/home/Footer";
import FixedContactButtons from "./components/FixedContactButtons";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Public Pages
import Home from "./pages/Home";
import CoursesPage from "./pages/CoursesPage";
import AboutPage from "./pages/AboutPage";
import SyllabusPage from "./pages/SyllabusPage";
import PyqPage from "./pages/PyqPage";
import TestSeries from "./pages/TestSeries";
import Daily from "./components/currentaffairs/Daily";
import SubjectDetail from "./components/syllabus/optionalSyllabus/SubjectDetail";
import JnanaDeepamPage from "./pages/JnanaDeepamPage";
import MainsPage from "./pages/MainsPage";
import LoginPage from "./pages/LoginPage";

// Admin Context & Layout Components
import { AdminAuthProvider } from "./admin/context/AdminAuthContext";
import { AdminDataProvider } from "./admin/context/AdminDataContext";
import ProtectedRoute from "./admin/components/ProtectedRoute";
import AdminLayout from "./admin/components/AdminLayout";

// Admin Pages
import AdminLogin from "./admin/pages/AdminLogin";
import AdminDashboard from "./admin/pages/AdminDashboard";
import DailyCAManagement from "./admin/pages/DailyCAManagement";
import MonthlyCAManagement from "./admin/pages/MonthlyCAManagement";
import PrelimsManagement from "./admin/pages/PrelimsManagement";
import MainsManagement from "./admin/pages/MainsManagement";
import StudentResults from "./admin/pages/StudentResults";
import QuestionBankManagement from "./admin/pages/QuestionBankManagement";

// Student Context & Layout Components
import { StudentAuthProvider } from "./student/context/StudentAuthContext";
import StudentProtectedRoute from "./student/components/StudentProtectedRoute";
import StudentLayout from "./student/components/StudentLayout";

// Student Portal Pages
import StudentDashboard from "./student/pages/StudentDashboard";
import StudentDailyCA from "./student/pages/StudentDailyCA";
import StudentMonthlyCA from "./student/pages/StudentMonthlyCA";
import StudentPrelims from "./student/pages/StudentPrelims";
import StudentTestTaking from "./student/pages/StudentTestTaking";
import StudentMains from "./student/pages/StudentMains";
import StudentResultsPage from "./student/pages/StudentResults";
import StudentProfile from "./student/pages/StudentProfile";

function App() {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isStudentRoute = location.pathname.startsWith("/student");

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <AdminAuthProvider>
      <AdminDataProvider>
        <StudentAuthProvider>
          {isAdminRoute ? (
            /* Admin Portal */
            <div className="min-h-screen bg-[#f8fafc]">
              <Routes>
                {/* Unprotected Admin Login Route */}
                <Route path="/admin/login" element={<AdminLogin />} />

                {/* Protected Admin Routes with Outlet inside AdminLayout */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute>
                      <AdminLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/admin/dashboard" replace />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="daily-ca" element={<DailyCAManagement />} />
                  <Route path="monthly-ca" element={<MonthlyCAManagement />} />
                  <Route path="question-bank" element={<QuestionBankManagement />} />
                  <Route path="prelims" element={<PrelimsManagement />} />
                  <Route path="mains" element={<MainsManagement />} />
                  <Route path="results" element={<StudentResults />} />
                  <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
                </Route>
              </Routes>
            </div>
          ) : isStudentRoute ? (
            /* Student Portal */
            <div className="min-h-screen bg-[#f8fafc]">
              <Routes>
                <Route
                  path="/student"
                  element={
                    <StudentProtectedRoute>
                      <StudentLayout />
                    </StudentProtectedRoute>
                  }
                >
                  <Route index element={<Navigate to="/student/dashboard" replace />} />
                  <Route path="dashboard" element={<StudentDashboard />} />
                  <Route path="daily-ca" element={<StudentDailyCA />} />
                  <Route path="monthly-ca" element={<StudentMonthlyCA />} />
                  <Route path="prelims" element={<StudentPrelims />} />
                  <Route path="prelims/test/:id" element={<StudentTestTaking />} />
                  <Route path="mains" element={<StudentMains />} />
                  <Route path="results" element={<StudentResultsPage />} />
                  <Route path="profile" element={<StudentProfile />} />
                  <Route path="*" element={<Navigate to="/student/dashboard" replace />} />
                </Route>
              </Routes>
            </div>
          ) : (
            /* Public Website */
            <div className="min-h-screen flex flex-col">
              <Navbar />
              <main className="flex-grow">
                <Routes>
                  {/* Public Pages */}
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/courses" element={<CoursesPage />} />
                  <Route path="/about-upsc" element={<AboutPage />} />
                  <Route path="/syllabus" element={<SyllabusPage />} />
                  <Route path="/pyq" element={<PyqPage />} />
                  <Route path="/testseries" element={<TestSeries />} />
                  <Route path="/current" element={<Daily />} />
                  <Route path="/jnanadeepam" element={<JnanaDeepamPage />} />
                  <Route path="/free-initiatives" element={<JnanaDeepamPage />} />
                  <Route path="/mains" element={<MainsPage />} />
                  <Route path="/daw" element={<MainsPage />} />
                  <Route
                    path="/optional-subject/:subjectName"
                    element={<SubjectDetail />}
                  />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </main>
              <Footer />
              <FixedContactButtons />
            </div>
          )}

          {/* Toast Notifications */}
          <ToastContainer />
        </StudentAuthProvider>
      </AdminDataProvider>
    </AdminAuthProvider>
  );
}

export default App;
